import { Crown } from 'lucide-react';
import { type FC } from 'react';

import { BrandBackdrop } from '@/components/brand-backdrop';

/**
 * The dot field, spanning the banner and fading out to the left.
 *
 * `field` rather than `edges`: `edges` ramps in from both sides and leaves the
 * middle clear, which put dots behind the name and a gap through the centre —
 * the frame has one field, densest at the right edge and gone before it reaches
 * the copy. The mask is what makes that shape, and it is why the canvas covers
 * the whole box rather than stopping short of it.
 */
const FIELD = '[mask-image:linear-gradient(to_right,transparent_45%,#000_100%)]';

interface ProfileBannerProps {
  /** The crown is the superuser's alone — the frame's other variant has none. */
  crowned: boolean;
  name: string;
  role: string;
}

/**
 * The frame's `profile-header-banner`: the signed-in name over their role, on
 * the brand tint, against the dithered brand field.
 *
 * The second line is the role, not the address. The address has its own row in
 * Account Info, and repeating it here left the banner saying nothing the card
 * below did not.
 *
 * The frame's field is a screenshot of a web page footer pasted in as a texture
 * — 3.8MB, with "PRIVACY SITEMAP" legible in it. What it stands in for is
 * `createBrandBackdrop`, the design system's own dithered field, which is what
 * this renders.
 *
 * The name is a paragraph, not a heading. "My Profile" above is the page's h1,
 * and the name is what the page is about rather than a section of it.
 */
export const ProfileBanner: FC<ProfileBannerProps> = ({ crowned, name, role }) => (
  // `isolate` so the canvas cannot paint over the copy, which the canvas being
  // first in the DOM would otherwise leave to chance. p-10 is the frame's 40.
  <div className="relative isolate flex items-center overflow-hidden rounded-2xl bg-[var(--ds-color-bg-brand-light)] p-10">
    <BrandBackdrop className={FIELD} shape="field" />

    <div className="relative flex min-w-0 flex-col gap-1">
      <p className="truncate text-xl leading-6 font-medium text-[var(--ds-color-text-primary)]">
        {name}
      </p>

      <div className="flex min-w-0 items-center gap-1">
        {/* The frame's `Icon / crown`, flattened to a fill there. Lucide is the
            same set the folder-open mark on the project header comes from, at
            the 1.5 stroke those are drawn with. Decorative — the role is
            written beside it. */}
        {crowned && (
          <Crown
            aria-hidden="true"
            className="size-4 shrink-0 text-[color:var(--ds-color-raw-stone-600)]"
            strokeWidth={1.5}
          />
        )}
        <p className="truncate text-sm leading-5 text-[color:var(--ds-color-raw-stone-600)]">
          {role}
        </p>
      </div>
    </div>
  </div>
);
